import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  Shield, RefreshCw, Eye, RotateCcw, Filter,
  BarChart3, PlusCircle, Pencil, XCircle, User,
  CalendarIcon, X, ShieldCheck, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_OPTIONS = [
  { value: 'all', label: 'Tutte' },
  { value: 'creazione', label: 'Creazione' },
  { value: 'modifica', label: 'Modifica' },
  { value: 'eliminazione', label: 'Eliminazione' },
  { value: 'ripristino', label: 'Ripristino' },
];

const RESOURCE_OPTIONS = [
  { value: 'all', label: 'Tutte' },
  { value: 'clients', label: 'Clienti' },
  { value: 'policies', label: 'Polizze' },
  { value: 'quotes', label: 'Preventivi' },
  { value: 'assets', label: 'Beni' },
  { value: 'claims', label: 'Sinistri' },
  { value: 'payments', label: 'Pagamenti' },
  { value: 'telecom_contracts', label: 'Contratti Telefonia' },
  { value: 'telecom_billing', label: 'Fatture Telefonia' },
  { value: 'telecom_sims', label: 'SIM' },
  { value: 'utility_practices', label: 'Pratiche Utenze' },
  { value: 'sub_agenti', label: 'Sub-Agenti' },
  { value: 'sub_agenti_pagamenti', label: 'Pagamenti Sub-Agenti' },
  { value: 'mutui', label: 'Mutui' },
  { value: 'canone_rai', label: 'Canone RAI' },
  { value: 'ospitalita', label: 'Ospitalità' },
  { value: 'users', label: 'Utenti' },
];

const actionStyles = {
  creazione: { className: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: PlusCircle },
  modifica: { className: 'bg-sky-50 text-sky-700 border-sky-200', Icon: Pencil },
  eliminazione: { className: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
  ripristino: { className: 'bg-violet-50 text-violet-700 border-violet-200', Icon: RotateCcw },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

export default function AuditLog() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: 'all',
    resource: 'all',
    user_id: '',
    date_from: '',
    date_to: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const [detailEntry, setDetailEntry] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [integrity, setIntegrity] = useState(null);

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/audit-log/stats`, { headers: authHeader() });
      setStats(r.data);
    } catch (e) {
      // no toast per non essere invadenti
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: ITEMS_PER_PAGE, skip: (currentPage - 1) * ITEMS_PER_PAGE };
      if (filters.action && filters.action !== 'all') params.action = filters.action;
      if (filters.resource && filters.resource !== 'all') params.resource = filters.resource;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const r = await axios.get(`${API}/api/audit-log`, { headers: authHeader(), params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Errore nel caricamento del registro attività');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => { loadStats(); loadItems(); }, [loadStats, loadItems]);

  // Reset a pagina 1 quando cambiano i filtri
  useEffect(() => { setCurrentPage(1); }, [filters]);

  const resetFilters = () => setFilters({ action: 'all', resource: 'all', user_id: '', date_from: '', date_to: '' });

  const doRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(true);
    try {
      await axios.post(`${API}/api/audit-log/${confirmRestore.id}/restore`, {}, { headers: authHeader() });
      toast.success('Voce ripristinata con successo');
      setConfirmRestore(null);
      loadItems();
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ripristino fallito');
    } finally {
      setRestoring(false);
    }
  };

  const doVerify = async () => {
    setVerifying(true);
    try {
      const r = await axios.get(`${API}/api/audit-log/verify`, { headers: authHeader() });
      setIntegrity(r.data);
      if (r.data.ok) {
        toast.success(`Integrità verificata: ${r.data.checked} voci in catena, nessuna alterazione`);
      } else {
        toast.error(`Catena compromessa alla voce #${r.data.broken_at}: ${r.data.error}`);
      }
    } catch (e) {
      toast.error('Verifica integrità non riuscita');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-violet-100">
            <Shield className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Registro Attività (Audit Log)</h1>
            <p className="text-sm text-slate-500 mt-1">
              Registro <strong>immodificabile</strong> (catena di hash) di modifiche e cancellazioni sul CRM. Visibile solo al SuperAdmin.
            </p>
            {integrity && (
              <p className={`text-xs mt-1 inline-flex items-center gap-1 ${integrity.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                {integrity.ok
                  ? <><CheckCircle2 className="w-3.5 h-3.5" /> Integrità OK — {integrity.checked} voci verificate</>
                  : <><AlertTriangle className="w-3.5 h-3.5" /> Catena compromessa alla voce #{integrity.broken_at}</>}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadStats(); loadItems(); }} data-testid="audit-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
          </Button>
          <Button variant="outline" onClick={doVerify} disabled={verifying} data-testid="audit-verify-btn">
            <ShieldCheck className="w-4 h-4 mr-2" /> {verifying ? 'Verifica…' : 'Verifica integrità'}
          </Button>
        </div>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Totale (ultimi 30gg)"
          value={stats?.total ?? '—'}
          Icon={BarChart3}
          color="text-slate-700"
          data-testid="stat-total"
        />
        <StatCard
          label="Creazioni"
          value={stats?.creazioni ?? '—'}
          Icon={PlusCircle}
          color="text-emerald-600"
          data-testid="stat-creazioni"
        />
        <StatCard
          label="Modifiche"
          value={stats?.modifiche ?? '—'}
          Icon={Pencil}
          color="text-sky-600"
          data-testid="stat-modifiche"
        />
        <StatCard
          label="Eliminazioni"
          value={stats?.eliminazioni ?? '—'}
          Icon={XCircle}
          color="text-rose-600"
          data-testid="stat-eliminazioni"
        />
        <Card data-testid="stat-top-user">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Utente più attivo</p>
              <User className="w-4 h-4 text-slate-400" />
            </div>
            {stats?.top_user ? (
              <>
                <p className="text-lg font-bold text-slate-900 mt-2 truncate">{stats.top_user.user_name || stats.top_user.user_email || '—'}</p>
                <p className="text-xs text-slate-500 mt-1">{stats.top_user.count} azioni</p>
              </>
            ) : (
              <p className="text-lg font-bold text-slate-400 mt-2">—</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-semibold text-slate-700">Filtri</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Azione</label>
              <Select value={filters.action} onValueChange={(v) => setFilters(f => ({ ...f, action: v }))}>
                <SelectTrigger data-testid="filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Risorsa</label>
              <Select value={filters.resource} onValueChange={(v) => setFilters(f => ({ ...f, resource: v }))}>
                <SelectTrigger data-testid="filter-resource"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESOURCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">ID Utente</label>
              <Input
                placeholder="user_id"
                value={filters.user_id}
                onChange={(e) => setFilters(f => ({ ...f, user_id: e.target.value }))}
                data-testid="filter-user-id"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Dal</label>
              <DateFilterPicker
                value={filters.date_from}
                onChange={(v) => setFilters(f => ({ ...f, date_from: v }))}
                testId="filter-date-from"
                placeholder="Seleziona data"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Al</label>
              <DateFilterPicker
                value={filters.date_to}
                onChange={(v) => setFilters(f => ({ ...f, date_to: v }))}
                testId="filter-date-to"
                placeholder="Seleziona data"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={resetFilters} data-testid="filter-reset">Reset</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella voci */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-600">
              Voci registrate <span className="text-slate-400">({items.length}{total > items.length ? ` di ${total}` : ''})</span>
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Data/Ora</th>
                  <th className="text-left px-4 py-3">Utente</th>
                  <th className="text-left px-4 py-3">Ruolo</th>
                  <th className="text-left px-4 py-3">Azione</th>
                  <th className="text-left px-4 py-3">Risorsa</th>
                  <th className="text-left px-4 py-3">ID Risorsa</th>
                  <th className="text-left px-4 py-3">Endpoint</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-400">Caricamento…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-400">Nessuna voce registrata</td></tr>
                )}
                {!loading && items.map((it) => {
                  const style = actionStyles[it.action] || { className: 'bg-slate-100 text-slate-700 border-slate-200', Icon: BarChart3 };
                  const ActIcon = style.Icon;
                  return (
                    <tr key={it.id} className="hover:bg-slate-50" data-testid={`audit-row-${it.id}`}>
                      <td className="px-4 py-3 text-slate-700 tabular-nums">{formatDate(it.timestamp)}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900 font-medium truncate max-w-[180px]">{it.user_name || '—'}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[180px]">{it.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {it.user_role && (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 uppercase">
                            {it.user_role}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${style.className}`}>
                          <ActIcon className="w-3 h-3" />
                          {it.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{it.resource_label || it.resource_type}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 truncate max-w-[160px]" title={it.resource_id}>
                        {it.resource_id || '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 truncate max-w-[280px]" title={it.endpoint}>
                        {it.endpoint}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDetailEntry(it)}
                            data-testid={`audit-view-${it.id}`}
                            title="Dettaglio"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={it.restored || it.action === 'ripristino' || !it.resource_id}
                            onClick={() => setConfirmRestore(it)}
                            data-testid={`audit-restore-${it.id}`}
                            className="text-amber-700 border-amber-200 hover:bg-amber-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            {it.restored ? 'Ripristinata' : 'Ripristina'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > ITEMS_PER_PAGE && (
            <div className="p-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Pagina {currentPage} di {Math.ceil(total / ITEMS_PER_PAGE)}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(total / ITEMS_PER_PAGE), p + 1))}
                  disabled={currentPage >= Math.ceil(total / ITEMS_PER_PAGE)}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog dettaglio */}
      <Dialog open={!!detailEntry} onOpenChange={(v) => !v && setDetailEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Dettaglio voce</DialogTitle>
          </DialogHeader>
          {detailEntry && (
            <div className="space-y-3 text-sm">
              <Row label="Data/Ora" value={formatDate(detailEntry.timestamp)} />
              <Row label="Utente" value={`${detailEntry.user_name || ''} (${detailEntry.user_email || ''})`} />
              <Row label="Ruolo" value={detailEntry.user_role} />
              <Row label="Azione" value={detailEntry.action} />
              <Row label="Risorsa" value={detailEntry.resource_label || detailEntry.resource_type} />
              <Row label="ID Risorsa" value={detailEntry.resource_id} mono />
              <Row label="Endpoint" value={detailEntry.endpoint} mono />
              <Row label="Status HTTP" value={detailEntry.status_code} />
              {detailEntry.notes && <Row label="Note" value={detailEntry.notes} />}
              {detailEntry.restored && (
                <Row label="Ripristinata da" value={`${detailEntry.restored_by_email || ''} il ${formatDate(detailEntry.restored_at)}`} />
              )}
              {detailEntry.payload_before && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 font-medium">Stato precedente</p>
                  <pre className="bg-slate-50 rounded p-3 text-[11px] max-h-64 overflow-auto border border-slate-100">
                    {JSON.stringify(detailEntry.payload_before, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog conferma ripristino */}
      <Dialog open={!!confirmRestore} onOpenChange={(v) => !v && setConfirmRestore(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma ripristino</DialogTitle>
          </DialogHeader>
          {confirmRestore && (
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                Vuoi davvero ripristinare lo stato antecedente a questa azione?
              </p>
              <ul className="text-xs bg-slate-50 border rounded p-3 space-y-1">
                <li><strong>Azione:</strong> {confirmRestore.action}</li>
                <li><strong>Risorsa:</strong> {confirmRestore.resource_label || confirmRestore.resource_type}</li>
                <li><strong>ID:</strong> <span className="font-mono">{confirmRestore.resource_id}</span></li>
                <li><strong>Utente:</strong> {confirmRestore.user_email}</li>
              </ul>
              <p className="text-amber-700 text-xs">
                {confirmRestore.action === 'creazione'
                  ? 'Verrà eliminato il documento creato.'
                  : confirmRestore.action === 'eliminazione'
                  ? 'Il documento eliminato verrà reinserito.'
                  : 'Il documento tornerà allo stato precedente alla modifica.'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRestore(null)} disabled={restoring}>Annulla</Button>
            <Button onClick={doRestore} disabled={restoring} data-testid="confirm-restore-btn">
              {restoring ? 'Ripristino…' : 'Conferma Ripristino'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function StatCard({ label, value, Icon, color = 'text-slate-700', 'data-testid': testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
          {Icon && <Icon className={`w-4 h-4 ${color}`} />}
        </div>
        <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex gap-4 items-start">
      <span className="text-xs uppercase tracking-wide text-slate-500 font-medium w-32 shrink-0">{label}</span>
      <span className={`text-slate-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function DateFilterPicker({ value, onChange, testId, placeholder }) {
  const [open, setOpen] = useState(false);
  // value is stored as ISO YYYY-MM-DD to keep API contract intact
  const parsed = value ? new Date(`${value}T00:00:00`) : undefined;
  const displayLabel = parsed && !isNaN(parsed) ? format(parsed, 'dd/MM/yyyy', { locale: it }) : '';

  const handleSelect = (d) => {
    if (!d) {
      onChange('');
      return;
    }
    // Build local YYYY-MM-DD (avoid UTC drift)
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-start font-normal h-10 ${displayLabel ? 'text-slate-900' : 'text-slate-400'}`}
          data-testid={testId}
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-slate-500 shrink-0" />
          <span className="flex-1 text-left truncate">{displayLabel || placeholder || 'Seleziona data'}</span>
          {displayLabel && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clear(e); }}
              className="ml-1 rounded hover:bg-slate-100 p-0.5 cursor-pointer"
              data-testid={`${testId}-clear`}
              aria-label="Rimuovi filtro data"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={handleSelect}
          locale={it}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
